import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Feather, Loader2, CheckCircle2, Clock, Zap } from "lucide-react";

export default function CreditsThanks() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const purchaseId = params.get("purchase_id");
  const [state, setState] = useState({ status: "checking", credits: 0 });

  useEffect(() => {
    if (!purchaseId) { setState({ status: "unknown" }); return; }
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/credits/status/${purchaseId}`);
        if (data.status === "COMPLETED") { setState({ ...data, status: "COMPLETED" }); return; }
        setState({ ...data, status: "pending" });
      } catch { /* ignore */ }
      if (tries < 6) timer = setTimeout(poll, 2000);
      else setState((s) => ({ ...s, status: s.status === "checking" ? "pending" : s.status }));
    };
    poll();
    return () => clearTimeout(timer);
  }, [purchaseId]);

  const done = state.status === "COMPLETED";

  return (
    <div className="min-h-screen bg-background text-foreground grid place-items-center px-5">
      <div className="max-w-md w-full text-center" data-testid="credits-thanks">
        <Link to="/app" className="inline-flex items-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-lg bg-primary grid place-items-center"><Feather className="w-5 h-5 text-primary-foreground" /></div>
          <span className="font-display text-2xl font-bold">CodexStudio</span>
        </Link>

        <div className="rounded-3xl border border-border bg-card p-10">
          {state.status === "checking" ? (
            <><Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-5" />
              <h1 className="font-display text-2xl font-bold">Confirming your purchase…</h1></>
          ) : done ? (
            <>
              <div className="w-16 h-16 rounded-full bg-primary/15 grid place-items-center mx-auto mb-5">
                <Zap className="w-8 h-8 text-primary" />
              </div>
              <h1 className="font-display text-3xl font-bold" data-testid="credits-success">Credits added! ⚡</h1>
              <p className="text-muted-foreground mt-3">
                <span className="font-semibold text-foreground">{state.credits}</span> AI credits are now on your
                account. They never expire — happy writing!
              </p>
            </>
          ) : state.status === "CANCELED" ? (
            <>
              <Clock className="w-10 h-10 text-muted-foreground mx-auto mb-5" />
              <h1 className="font-display text-2xl font-bold">Payment cancelled</h1>
              <p className="text-muted-foreground mt-3">No charge was made. You can try again anytime.</p>
            </>
          ) : (
            <>
              <CheckCircle2 className="w-10 h-10 text-primary mx-auto mb-5" />
              <h1 className="font-display text-2xl font-bold">Thanks!</h1>
              <p className="text-muted-foreground mt-3">
                If you completed the payment, your credits may take a moment to confirm.
              </p>
            </>
          )}
          <Button className="mt-8" onClick={() => navigate("/app")} data-testid="credits-thanks-back">
            Back to my books
          </Button>
        </div>
      </div>
    </div>
  );
}
